import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, overlay, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindViewportLock();
    this.#clearSafariChrome();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#bindViewportLock();
    this.#paintSafariChrome();
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handleDrawerClose = () => {
    this.#unbindViewportLock();
    this.#clearSafariChrome();
  };

  #themeColorMeta = null;
  /** @type {string} */
  #previousThemeColor = '';
  /** @type {HTMLDivElement | null} */
  #chromeFill = null;
  #keyboardOpen = false;

  #isMobileOverlay() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  /**
   * @param {string} color
   * @returns {string}
   */
  #cssColorToHex(color) {
    const rgb = color.match(/rgba?\(\s*(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)/);
    if (!rgb) return color;
    return `#${[rgb[1], rgb[2], rgb[3]].map((n) => Number(n).toString(16).padStart(2, '0')).join('')}`;
  }

  #paintSafariChrome() {
    if (!this.#isMobileOverlay()) return;

    const dialog = this.#dialog;
    if (!dialog) return;

    const color = getComputedStyle(dialog).backgroundColor;
    const hex = this.#cssColorToHex(color);

    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta && this.#themeColorMeta !== meta) {
      this.#themeColorMeta = meta;
      this.#previousThemeColor = meta.getAttribute('content') ?? '';
    }
    meta?.setAttribute('content', hex);

    if (!this.#chromeFill) {
      const fill = document.createElement('div');
      fill.setAttribute('data-cart-drawer-chrome-fill', '');
      fill.setAttribute('aria-hidden', 'true');
      fill.style.cssText = [
        'position:fixed',
        'left:0',
        'right:0',
        'bottom:0',
        'width:100%',
        'height:max(120px, calc(100lvh - 100dvh))',
        'pointer-events:none',
        'z-index:0',
      ].join(';');
      document.body.prepend(fill);
      this.#chromeFill = fill;
    }
    this.#chromeFill.style.backgroundColor = color;
  }

  #clearSafariChrome() {
    this.#themeColorMeta?.setAttribute('content', this.#previousThemeColor);
    this.#themeColorMeta = null;
    this.#previousThemeColor = '';
    this.#chromeFill?.remove();
    this.#chromeFill = null;
  }

  #isEditableField(node) {
    if (!(node instanceof HTMLElement)) return false;
    return node.matches(
      'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]), textarea'
    );
  }

  #setKeyboardOpen(open) {
    const dialog = this.#dialog;
    if (!dialog || this.#keyboardOpen === open) return;

    this.#keyboardOpen = open;
    dialog.classList.toggle('is-keyboard-open', open);

    if (open) {
      document.addEventListener('touchmove', this.#preventKeyboardScroll, { passive: false });
      document.addEventListener('wheel', this.#preventKeyboardScroll, { passive: false });
    } else {
      document.removeEventListener('touchmove', this.#preventKeyboardScroll);
      document.removeEventListener('wheel', this.#preventKeyboardScroll);
    }
  }

  #preventKeyboardScroll = (event) => {
    if (this.#isEditableField(event.target)) return;
    event.preventDefault();
  };

  #onDrawerFocusIn = (event) => {
    if (!this.#isEditableField(event.target)) return;
    this.#setKeyboardOpen(true);
  };

  #onDrawerFocusOut = () => {
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (this.#dialog?.contains(active) && this.#isEditableField(active)) return;
      this.#setKeyboardOpen(false);
    });
  };

  #bindViewportLock() {
    const dialog = this.#dialog;
    dialog?.addEventListener('focusin', this.#onDrawerFocusIn);
    dialog?.addEventListener('focusout', this.#onDrawerFocusOut);
  }

  #unbindViewportLock() {
    const dialog = this.#dialog;
    dialog?.removeEventListener('focusin', this.#onDrawerFocusIn);
    dialog?.removeEventListener('focusout', this.#onDrawerFocusOut);
    document.removeEventListener('touchmove', this.#preventKeyboardScroll);
    document.removeEventListener('wheel', this.#preventKeyboardScroll);
    this.#keyboardOpen = false;
    dialog?.classList.remove('is-keyboard-open');
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content:not(.cart-drawer__content--empty)');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    // Items scroll inside .cart-drawer__items; upsell + summary stay in document flow.
    dialog.setAttribute('cart-summary-sticky', 'false');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
